/**
 * Which formats go in, which come out, and the small pure helpers derived from
 * those tables. Kept apart from [index.ts](index.ts) so the codec and the
 * legacy fallback can share them without importing each other.
 */
export const JPEG_MIME_TYPE = "image/jpeg";
export const PNG_MIME_TYPE = "image/png";

/**
 * Everything we can read. Only JPEG and PNG come back out — see the note on
 * `compressImage` for why the output side stays deliberately narrow.
 *
 * Between the browser's own decoder and the WASM codecs in
 * [decoders.ts](decoders.ts) this covers every raster format either can read:
 * BMP is uncompressed and benefits the most (`image/x-ms-bmp` is the legacy
 * MIME type some Windows tools still stamp on `.bmp`); HEIC/HEIF is what an
 * iPhone uploads by default; JPEG XL and QOI have no browser support at all
 * and are decoded purely in WASM.
 *
 * GIF and SVG are deliberately absent: the browser decodes only a GIF's first
 * frame (compressing would drop the animation), and rasterising an SVG throws
 * away the vector.
 */
export const COMPRESSIBLE_IMAGE_MIME_TYPES = [
  JPEG_MIME_TYPE,
  "image/jpg",
  PNG_MIME_TYPE,
  "image/webp",
  "image/bmp",
  "image/x-ms-bmp",
  "image/avif",
  "image/heic",
  "image/heif",
  "image/jxl",
  "image/qoi",
];

/**
 * Formats whose pixels can be see-through, and so are worth scanning for
 * alpha. Everything else compressible here (JPEG, BMP) is opaque by
 * definition.
 */
const ALPHA_CAPABLE_MIME_TYPES = [
  PNG_MIME_TYPE,
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "image/jxl",
  "image/qoi",
];

const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  [PNG_MIME_TYPE]: "png",
  [JPEG_MIME_TYPE]: "jpg",
  "image/jpg": "jpg",
};

export const isCompressibleRasterImage = (file: File): boolean =>
  COMPRESSIBLE_IMAGE_MIME_TYPES.includes(file.type.toLowerCase());

export const mayCarryAlpha = (file: File): boolean =>
  ALPHA_CAPABLE_MIME_TYPES.includes(file.type.toLowerCase());

const replaceFileExtension = (fileName: string, extension: string): string => {
  const dotIndex = fileName.lastIndexOf(".");
  const baseName = dotIndex === -1 ? fileName : fileName.slice(0, dotIndex);

  return `${baseName}.${extension}`;
};

/**
 * Wraps compressed bytes as a `File` that still looks like the upload it came
 * from — same base name and timestamp, extension corrected to whatever it was
 * actually encoded as.
 */
export const toCompressedFile = (
  source: File,
  bytes: BlobPart,
  mimeType: string
): File =>
  new File(
    [bytes],
    replaceFileExtension(
      source.name,
      EXTENSION_BY_MIME_TYPE[mimeType.toLowerCase()] ?? "jpg"
    ),
    { type: mimeType, lastModified: source.lastModified }
  );
